// Global packages and functions
import { useCallback } from 'react'
import { useSearchParams } from '@remix-run/react'

interface Props {
  /**
   * The pending URL where one is in flight, otherwise the committed one.
   * Mutating from this means rapid clicks during a navigation compose
   * instead of clobbering each other.
   */
  uiParams: URLSearchParams
}

/*
  Listing URL state.

  Products are resolved in the loader now, so every setter here is simply a
  navigation: writing the param re-runs the loader and new products come back.
  There is deliberately no mount effect — the previous one stripped page,
  on-sale, price-range and attribute on every mount, which would throw away
  server rendered results on any deep linked listing URL.
*/
const useProductListing = ({ uiParams }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Every refinement returns to page one, otherwise changing sort or a filter
  // can leave you stranded on a page that no longer exists
  const update = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const next = new URLSearchParams(uiParams)

      mutate(next)
      next.delete('page')

      setSearchParams(next, { preventScrollReset: true, replace: true })
    },
    [uiParams, setSearchParams]
  )

  const setQueryOrder = useCallback(
    (value: string) => update(params => params.set('order', value)),
    [update]
  )

  const setQueryOnSale = useCallback(
    (value: string) =>
      update(params =>
        value ? params.set('on-sale', value) : params.delete('on-sale')
      ),
    [update]
  )

  const setQueryPriceRange = useCallback(
    (value: string | null) =>
      update(params =>
        value ? params.set('price-range', value) : params.delete('price-range')
      ),
    [update]
  )

  // Attributes are repeatable, so rebuild the whole set rather than patching
  const setQueryAttributes = useCallback(
    (value: string) =>
      update(params => {
        const current = params.getAll('attribute')

        params.delete('attribute')

        const next = current.includes(value)
          ? current.filter(e => e !== value)
          : [...current, value]

        next.forEach(entry => params.append('attribute', entry))
      }),
    [update]
  )

  const clearAll = useCallback(() => {
    const next = new URLSearchParams(uiParams)
    const listingParams = [
      'order',
      'page',
      'on-sale',
      'price-range',
      'attribute',
    ]

    listingParams.forEach(param => next.delete(param))

    setSearchParams(next, { preventScrollReset: true })
  }, [uiParams, setSearchParams])

  // Derived rather than held in state. The previous version read an
  // "attributes" param that is never written, so attribute filters never
  // registered here and the clear controls stayed hidden
  const isFiltered =
    uiParams.getAll('attribute').length > 0 ||
    Boolean(uiParams.get('price-range')) ||
    uiParams.get('on-sale') === 'true'

  return {
    searchParams,
    isFiltered,
    setQueryOrder,
    setQueryOnSale,
    setQueryPriceRange,
    setQueryAttributes,
    clearAll,
  }
}

export default useProductListing
